import { makeAutoObservable, computed } from 'mobx';
import {
  ProjectReportInterface,
  TasksReportInterface,
  WorkloadReportMemberInterface,
  PaymentReportDetails,
  InvoiceReportInterface
} from '../../interfaces/ReportInterface';
import { rootStore } from '../rootStore';
import { ReportFilterInterface } from '../../interfaces/TimeTrackingInterface';
import { RawTimeTrackingDataInterface } from '../interfaces/task';

export class ReportStore {
  loading: boolean = false;
  projectReportData: ProjectReportInterface[] = [];
  filteredTaskReportData: TasksReportInterface[] = [];
  reportFilter: ReportFilterInterface = {
    selectedDataType: 1,
    timeViewType: 1,
    chartType: 1
  };
  timeTrackingReportData: RawTimeTrackingDataInterface[] = [];
  reportTimelineNextCursor: string | null = null;
  workloadReportData: WorkloadReportMemberInterface[] | null = null;

  // Payment and Invoice reports (migrated from Redux)
  paymentReportData: PaymentReportDetails[] = [];
  filteredPaymentReportData: PaymentReportDetails[] = [];
  invoiceReportData: InvoiceReportInterface[] = [];
  filteredInvoiceReportData: InvoiceReportInterface[] = [];

  constructor() {
    makeAutoObservable(this, {
      taskReportData: computed
    });
  }

  get taskReportData(): TasksReportInterface[] {
    if (!rootStore.taskStore.tasks) {
      return [];
    }
    return rootStore.taskStore.tasks.map(
      (task): TasksReportInterface => ({
        id: task.id,
        projectId: task.project?.id || '',
        users: task.users,
        status: task.status,
        projectName: task.project?.name || '',
        name: task.name,
        dueDate: task.dueDate ?? null
      })
    );
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  //Project Report Variables
  setProjectReportData(projectReportData: ProjectReportInterface[]) {
    this.projectReportData = projectReportData;
  }

  setUserExist(projectId: string, userExist: boolean) {
    const projectIndex = this.projectReportData.findIndex((project) => project.id === projectId);
    if (projectIndex !== -1) {
      this.projectReportData[projectIndex].isUserExist = userExist;
    }
  }

  //Task Report Variables
  setFilteredTaskReportData(filteredTaskReportData: TasksReportInterface[]) {
    this.filteredTaskReportData = filteredTaskReportData;
  }

  //Time Report Variables
  setTimeTrackingReportData(timeTrackingReportData: RawTimeTrackingDataInterface[]) {
    this.timeTrackingReportData = timeTrackingReportData;
  }

  //Workload Report Variables
  setWorkloadReportData(workloadReportData: WorkloadReportMemberInterface[] | null) {
    this.workloadReportData = workloadReportData;
  }

  updateReportFilter(propsName: string, value: number) {
    this.reportFilter[propsName as keyof ReportFilterInterface] = value;
  }

  setReportTimelineNextCursor(nextCursor: string | null) {
    this.reportTimelineNextCursor = nextCursor;
  }

  // Payment Report Methods
  setPaymentReportData(data: PaymentReportDetails[]) {
    this.paymentReportData = data;
  }

  setFilteredPaymentReportData(data: PaymentReportDetails[]) {
    this.filteredPaymentReportData = data;
  }

  // Invoice Report Methods
  setInvoiceReportData(data: InvoiceReportInterface[]) {
    this.invoiceReportData = data;
  }

  setFilteredInvoiceReportData(data: InvoiceReportInterface[]) {
    this.filteredInvoiceReportData = data;
  }

  // Clear report related data
  clearReportData() {
    this.loading = false;
    this.projectReportData = [];
    this.filteredTaskReportData = [];
    this.timeTrackingReportData = [];
    this.workloadReportData = null;
    this.paymentReportData = [];
    this.filteredPaymentReportData = [];
    this.invoiceReportData = [];
    this.filteredInvoiceReportData = [];
  }
}
